/**
 * Application Insights telemetry for CRUCIBLE frontend.
 *
 * Singleton wrapper around @microsoft/applicationinsights-web.
 * Auto-captures: page views, exceptions, network failures, performance.
 * Custom tracking: chat errors, tool usage, session events.
 *
 * Initialization:
 *   Called once from TelemetryProvider on mount.
 *   No-ops gracefully when connection string is missing (local dev).
 *
 * Usage:
 *   import { trackEvent, trackException, trackChatError } from '@/lib/telemetry'
 *   trackEvent('tool_executed', { tool: 'update_odm_xml', sessionId })
 *   trackChatError(error, { sessionId, messageCount: 5 })
 */

import { ApplicationInsights, SeverityLevel } from '@microsoft/applicationinsights-web'
import { ClickAnalyticsPlugin } from '@microsoft/applicationinsights-clickanalytics-js'

let appInsights: ApplicationInsights | null = null
let clickPlugin: ClickAnalyticsPlugin | null = null

// Click Analytics plug-in — auto-captures `customEvents` for clicks on any
// element carrying `data-cap-*` attributes. Convention:
//   data-cap-surface  → owning page/component ("file-list", "session-guard")
//   data-cap-action   → what the click does ("rename", "reauth", "send")
//   data-cap-target   → free-form context (filename, sessionId tail, etc.)
//
// captureAllMetaDataContent=false  — never scrape every <meta> (CSP nonces etc.)
// urlCollectQuery=false           — query strings can carry sessionIds; avoid leaking
// dropInvalidEvents=true          — keep customEvents table signal-rich
// useDefaultContentNameOrId=true  — fall back to `id` attribute when no data-cap-* present
const CLICK_ANALYTICS_CONFIG = {
  autoCapture: true,
  urlCollectHash: false,
  urlCollectQuery: false,
  dropInvalidEvents: true,
  dataTags: {
    useDefaultContentNameOrId: true,
    captureAllMetaDataContent: false,
    customDataPrefix: 'data-cap-',
    aiBlobAttributeTag: 'data-cap-blob',
  },
}

/** Initialize App Insights. Safe to call multiple times — only initializes once. */
export function initTelemetry(): ApplicationInsights | null {
  if (appInsights) return appInsights
  if (typeof window === 'undefined') return null

  const connectionString = process.env.NEXT_PUBLIC_APPINSIGHTS_CONNECTION_STRING
  if (!connectionString) {
    console.debug('[Telemetry] No connection string — telemetry disabled')
    return null
  }

  clickPlugin = new ClickAnalyticsPlugin()

  appInsights = new ApplicationInsights({
    config: {
      connectionString,
      enableAutoRouteTracking: true,
      enableCorsCorrelation: true,
      enableRequestHeaderTracking: true,
      enableResponseHeaderTracking: true,
      disableFetchTracking: false,
      disableAjaxTracking: false,
      maxBatchInterval: 5000,
      // Sampling — capture 100% in dev, can be reduced in prod via config
      samplingPercentage: process.env.NEXT_PUBLIC_ENVIRONMENT === 'production' ? 50 : 100,
      extensions: [clickPlugin],
      extensionConfig: {
        [clickPlugin.identifier]: CLICK_ANALYTICS_CONFIG,
      },
    },
  })

  appInsights.loadAppInsights()

  // Bundle identity. NEXT_PUBLIC_BUILD_HASH is baked at build time, so this
  // identifies the exact bundle the tab is running — not the server it talks
  // to. application.ver populates the native AppVersion column on EVERY
  // telemetry type (events, pageviews, deps, exceptions, clicks); the
  // initializer mirrors it into customDimensions as `buildHash` for the
  // Properties-based KQL we already write. 'dev' doubles as a canary for
  // deploys that forgot the build-arg.
  const buildHash = process.env.NEXT_PUBLIC_BUILD_HASH || 'dev'
  appInsights.context.application.ver = buildHash
  appInsights.addTelemetryInitializer((item) => {
    const data = (item.data ?? {}) as Record<string, unknown>
    data.buildHash = buildHash
    item.data = data
    return true
  })

  appInsights.trackPageView()

  console.debug('[Telemetry] App Insights initialized (with Click Analytics)')
  return appInsights
}

/** Get the raw App Insights instance (null if not initialized). */
export function getAppInsights(): ApplicationInsights | null {
  return appInsights
}

/** Set authenticated user context. Call after login. */
export function setUserContext(userId: string, email?: string) {
  if (!appInsights) return
  appInsights.setAuthenticatedUserContext(userId, email, true)
}

/** Clear user context. Call on logout. */
export function clearUserContext() {
  if (!appInsights) return
  appInsights.clearAuthenticatedUserContext()
}

// ─── Custom Event Tracking ──────────────────────────────────────

/** Track a named event with optional properties and measurements. */
export function trackEvent(
  name: string,
  properties?: Record<string, string>,
  measurements?: Record<string, number>
) {
  if (!appInsights) return
  appInsights.trackEvent({ name }, { ...properties, ...measurements })
}

/** Track an exception with optional context properties. */
export function trackException(
  error: Error,
  properties?: Record<string, string>,
  severityLevel: SeverityLevel = SeverityLevel.Error
) {
  if (!appInsights) return
  appInsights.trackException({ exception: error, severityLevel, properties })
}

// ─── CRUCIBLE-Specific Helpers ──────────────────────────────────

/** Track a chat/streaming error with session context. */
export function trackChatError(
  error: unknown,
  context: {
    sessionId?: string
    /** Correlation handle bound at submit — joins chat_error to the
     *  chat_messages row and server "Chat request received" logs. */
    chatAttemptId?: string
    messageCount?: number
    action?: string
  } = {}
) {
  const err = error instanceof Error ? error : new Error(String(error))
  const category = categorizeError(err.message)

  trackException(err, {
    category,
    sessionId: context.sessionId ?? 'unknown',
    ...(context.chatAttemptId ? { chatAttemptId: context.chatAttemptId } : {}),
    messageCount: String(context.messageCount ?? 0),
    action: context.action ?? 'chat_stream',
  })

  trackEvent('chat_error', {
    category,
    sessionId: context.sessionId ?? 'unknown',
    ...(context.chatAttemptId ? { chatAttemptId: context.chatAttemptId } : {}),
    errorMessage: err.message.slice(0, 200),
  })
}

/** Track an AI tool execution. */
export function trackToolExecution(
  toolName: string,
  context: {
    sessionId?: string
    success?: boolean
    durationMs?: number
  } = {}
) {
  trackEvent('tool_executed', {
    toolName,
    sessionId: context.sessionId ?? 'unknown',
    success: String(context.success ?? true),
    durationMs: String(context.durationMs ?? 0),
  })
}

/** Track a user session lifecycle event. */
export function trackSessionEvent(
  action: 'created' | 'resumed' | 'renamed' | 'deleted',
  sessionId: string,
  properties?: Record<string, string>
) {
  trackEvent('session_lifecycle', {
    action,
    sessionId,
    ...properties,
  })
}

// ─── Notification Surface Telemetry (AB#19124 slice 4) ───────────
//
// One unified event for every user-facing notification, alert, banner,
// status nudge, and toast. The `surface` dimension distinguishes the
// origin ('toast' | 'session-guard' | 'version-guard' | ...) so a single
// KQL slicer covers all attention-grabbing UI:
//
//   customEvents
//   | where name == 'notification_shown'
//   | summarize count() by tostring(customDimensions.surface),
//                          tostring(customDimensions.kind)
//
// Pair with Click Analytics' Click events (data-cap-* attributes on
// each surface's actionable buttons) to get click-through rates per
// notification — no extra wiring needed.

export type NotificationKind =
  | 'info'
  | 'success'
  | 'warning'
  | 'error'
  | 'action-required'

/**
 * Fire when a notification, alert, banner, or status nudge becomes
 * visible to the user. Call from a useEffect that gates on the
 * component's visibility state — NOT on every render — so we don't
 * re-emit when the parent re-renders.
 *
 * Common props worth carrying: reason (why), persistent ('true'|'false'),
 * dismissible ('true'|'false'), surface-specific tags. Keep values short
 * — these end up in customDimensions which AppInsights caps per row.
 */
/**
 * Normalize the "why" dimension across notification surfaces so a single
 * `reason` column groups them all in AppInsights. Explicit reason wins;
 * else the most informative dimension (summary → targetTool); else
 * 'unspecified'.
 */
export function notificationReason(props?: Record<string, string>): string {
  return props?.reason || props?.summary || props?.targetTool || 'unspecified'
}

export function trackNotification(
  surface: string,
  kind: NotificationKind,
  props?: Record<string, string>
) {
  trackEvent('notification_shown', {
    surface,
    kind,
    ...props,
    reason: notificationReason(props),
  })
}

// useNotificationVisibility lives in its own module so tests can mock
// trackNotification at the import boundary — the in-module reference
// pattern (function calling its own neighbor by name) is opaque to
// vi.spyOn under ESM. Re-exported here so callers using
// `import { useNotificationVisibility } from '@/lib/telemetry'`
// don't need to change their import path.
export { useNotificationVisibility } from './use-notification-visibility'

// ─── Internal Helpers ───────────────────────────────────────────

function categorizeError(message: string): string {
  if (message.includes('timed out') || message.includes('timeout')) return 'timeout'
  if (message.includes('fetch') || message.includes('network') || message.includes('Failed to fetch')) return 'network'
  if (message.includes('401') || message.includes('403')) return 'auth'
  if (message.includes('429')) return 'rate_limit'
  if (message.includes('500') || message.includes('502') || message.includes('503')) return 'server'
  return 'unknown'
}
